import { type TypeSelectOption, toastService } from '@thefarmersfront/kitchen';

import { useAPI, useAsyncHandler } from '~/hooks/common';
import { renderFilterErrorMessage, transformToSelectOptions } from '~/utils/common';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeGetCodesParams, TypeGetCodesResponse } from '~/types/order.types';

type TypeFormattedCodes = Record<keyof TypeGetCodesResponse['data']['codes'], TypeSelectOption<string>[]>;

/**
 * [출고주문관리 / 반품교환관리] 공통 코드 조회 api 요청 훅
 */
const useGetCodes = () => {
  const { runAsync } = useAsyncHandler();

  const getCodes = useAPI<TypeGetCodesParams, TypeGetCodesResponse>({
    endpoint: API_ENDPOINT.ORDER.GET_CODES,
    method: 'get',
  });

  const formatCodes = ({ codes }: TypeGetCodesResponse['data']): TypeFormattedCodes => {
    return Object.fromEntries(
      Object.entries(codes).map(([key, value]) => [key, transformToSelectOptions(value, 'code', 'description')])
    ) as TypeFormattedCodes;
  };

  const requestCodes = async (
    params: TypeGetCodesParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeFormattedCodes) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => getCodes({ params }), {
      onSuccess: ({ data }) => {
        onSuccess?.(formatCodes(data));
      },
      onError: (error: TypeErrorResponseData['error']) => {
        toastService.addToast({
          type: 'error',
          message: renderFilterErrorMessage(),
        });
        onError?.(error);
      },
    });
  };

  return { requestCodes };
};

export default useGetCodes;
